/**
 * SemanticAuditTrail.js — Expense App Semantic Audit Trail viewer
 *
 * Fetches from /api/audit-logs (app='expense' scoped by backend).
 * Admin/superadmin only. Click a row to expand old→new diff.
 */
import React, { useState, useCallback } from "react";
import API from "../../utils/api";

const ACTION_LABELS = {
  "expense.create":             { icon: "➕", label: "Expense Created",             color: "#22c55e" },
  "expense.delete":             { icon: "🗑️", label: "Expense Deleted",            color: "#ef4444" },
  "reimbursement.manager_approve": { icon: "✅", label: "Mgr Approved Reimb.",      color: "#22c55e" },
  "reimbursement.manager_decline": { icon: "❌", label: "Mgr Declined Reimb.",      color: "#ef4444" },
  "reimbursement.finance_approve": { icon: "✅", label: "Finance Approved Reimb.",   color: "#16a34a" },
  "reimbursement.finance_decline": { icon: "❌", label: "Finance Declined Reimb.",   color: "#dc2626" },
  "bf_override.approve":        { icon: "✅", label: "BF Override Approved",        color: "#22c55e" },
  "bf_override.decline":        { icon: "❌", label: "BF Override Declined",        color: "#ef4444" },
};

function meta(action) {
  return ACTION_LABELS[action] || { icon: "📋", label: action || "—", color: "#64748b" };
}

function RoleBadge({ role }) {
  const colors = { superadmin: "#7c3aed", admin: "#2563eb", manager: "#059669", team_leader: "#0284c7", employee: "#64748b" };
  const c = colors[role] || "#475569";
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600, background: c + "22", color: c, border: `1px solid ${c}44`, textTransform: "capitalize" }}>
      {role || "—"}
    </span>
  );
}

function DiffView({ oldValue, newValue }) {
  let o, n;
  try { o = typeof oldValue === "string" ? JSON.parse(oldValue) : oldValue; } catch { o = null; }
  try { n = typeof newValue === "string" ? JSON.parse(newValue) : newValue; } catch { n = null; }
  if (!o && !n) return null;
  const keys = Array.from(new Set([...(o ? Object.keys(o) : []), ...(n ? Object.keys(n) : [])]));
  const changed = keys.filter(k => JSON.stringify(o?.[k] ?? null) !== JSON.stringify(n?.[k] ?? null));
  if (!changed.length) {
    return <pre style={{ margin: 0, fontSize: 11, color: "#475569", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{JSON.stringify(n || o, null, 2)}</pre>;
  }
  return (
    <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
      <tbody>
        {changed.map(k => (
          <tr key={k}>
            <td style={{ paddingRight: 8, color: "#94a3b8", whiteSpace: "nowrap", verticalAlign: "top" }}>{k}</td>
            <td style={{ color: "#f87171", textDecoration: "line-through", paddingRight: 8, verticalAlign: "top" }}>{o?.[k] != null ? String(o[k]).slice(0, 60) : "—"}</td>
            <td style={{ color: "#4ade80", verticalAlign: "top" }}>{n?.[k] != null ? String(n[k]).slice(0, 60) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SemanticAuditTrail() {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [fetched, setFetched]   = useState(false);
  const [filters, setFilters]   = useState({
    user_name: "", action: "", entity_type: "", effective_role: "",
    date_from: "", date_to: "",
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setFetched(true);
    try {
      const params = new URLSearchParams({ limit: 300 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await API.get(`/audit-logs?${params}`);
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  function handleFilter(e) {
    setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function fmtTime(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
      + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  }

  const inp = {
    background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
    color: "#1e293b", fontSize: 13, padding: "7px 12px", outline: "none", minWidth: 130,
  };
  const th = { color: "#94a3b8", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #e2e8f0" };
  const td = { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>🔍 Expense Audit Trail</h2>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20, alignItems: "flex-end" }}>
        <input style={inp} name="user_name"   placeholder="Person name"    value={filters.user_name}   onChange={handleFilter} />
        <input style={inp} name="action"      placeholder="Action"         value={filters.action}      onChange={handleFilter} />
        <input style={inp} name="entity_type" placeholder="Entity type"    value={filters.entity_type} onChange={handleFilter} />
        <select style={inp} name="effective_role" value={filters.effective_role} onChange={handleFilter}>
          <option value="">All roles</option>
          <option value="superadmin">Superadmin</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="team_leader">Team Leader</option>
          <option value="employee">Employee</option>
        </select>
        <input style={inp} type="date" name="date_from" value={filters.date_from} onChange={handleFilter} />
        <input style={inp} type="date" name="date_to"   value={filters.date_to}   onChange={handleFilter} />
        <button onClick={fetchLogs} style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Search</button>
        <button onClick={() => { setFilters({ user_name: "", action: "", entity_type: "", effective_role: "", date_from: "", date_to: "" }); setTimeout(fetchLogs, 50); }} style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: "pointer" }}>Clear</button>
      </div>

      {!fetched && (
        <div style={{ color: "#94a3b8", padding: "60px 0", textAlign: "center", fontSize: 15 }}>
          Press <b>Search</b> to load audit events
        </div>
      )}

      {fetched && (
        <>
          {loading && <div style={{ color: "#94a3b8", marginBottom: 12 }}>Loading…</div>}
          <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 12 }}>
            {logs.length} event{logs.length !== 1 ? "s" : ""} (max 300)
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>When</th>
                  <th style={th}>Person</th>
                  <th style={th}>Acting as</th>
                  <th style={th}>Action</th>
                  <th style={th}>Record</th>
                  <th style={th}>Changes</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && !loading && (
                  <tr><td colSpan={6} style={{ ...td, color: "#94a3b8", textAlign: "center", padding: 40 }}>No audit events found</td></tr>
                )}
                {logs.map(log => {
                  const m = meta(log.action);
                  const isOpen = expanded === log.id;
                  return (
                    <tr key={log.id} onClick={() => setExpanded(isOpen ? null : log.id)} style={{ cursor: "pointer", background: isOpen ? "#f8fafc" : "transparent" }}>
                      <td style={{ ...td, fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{fmtTime(log.created_at)}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{log.user_name || "—"}</div>
                        {log.ip && <div style={{ fontSize: 11, color: "#94a3b8" }}>{log.ip}</div>}
                      </td>
                      <td style={td}><RoleBadge role={log.effective_role || log.user_role} /></td>
                      <td style={td}><span style={{ color: m.color, fontWeight: 600 }}>{m.icon} {m.label}</span></td>
                      <td style={{ ...td, color: "#334155" }}>{log.entity_label || `${log.entity_type} #${log.entity_id}`}</td>
                      <td style={td}>
                        {isOpen
                          ? <DiffView oldValue={log.old_value} newValue={log.new_value} />
                          : (log.old_value || log.new_value)
                            ? <span style={{ color: "#94a3b8", fontSize: 11 }}>Click to expand ›</span>
                            : <span style={{ color: "#e2e8f0" }}>—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
